import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Car, Upload, X } from 'lucide-react';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

const CreateVehicle = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    fleetSysId: '',
    fleetSysVehicleId: '',
    fuel: 'PETROL',
    make: '',
    model: '',
    type: 'SEDAN',
    year: '',
    color: '',
    regNo: '',
    insuranceExpiry: '',
    roadTaxExpiry: '',
    fitnessExpiry: '',
    pucExpiry: '',
    permitExpiry: '',
    permitNumber: '',
    vendorId: '',
    regionalOffice: ''
  });

  const [documents, setDocuments] = useState({
    roadTaxDoc: null,
    permitDoc: null,
    fitnessDoc: null,
    pucDoc: null,
    insurance: null,
    vehicleRcDoc: null,
    vehiclePhoto: null
  });

  // Fetch vendors for dropdown
  const { data: vendorsData } = useQuery(
    ['vendors', 'all'],
    () => api.get('/vendors', { params: { limit: 1000, isApproved: 'true' } }),
    { staleTime: 300000 }
  );

  // Fetch regional offices for dropdown
  const { data: regionalOfficesData } = useQuery(
    ['regional-offices', 'all'],
    () => api.get('/regional-offices', { params: { limit: 1000 } }),
    { staleTime: 300000 }
  );

  const createMutation = useMutation(
    (data) => {
      const formDataToSend = new FormData();
      
      // Add form fields
      Object.keys(data).forEach(key => {
        if (key !== 'documents') {
          formDataToSend.append(key, data[key]);
        }
      });

      // Add documents
      Object.keys(documents).forEach(key => {
        if (documents[key]) {
          formDataToSend.append(key, documents[key]);
        }
      });

      return api.post('/vehicles', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    },
    {
      onSuccess: () => {
        toast.success('Vehicle created successfully');
        navigate('/vehicles');
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || 'Failed to create vehicle');
      }
    }
  );

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileChange = (field, file) => {
    setDocuments(prev => ({
      ...prev,
      [field]: file
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.fleetSysId.trim()) {
      toast.error('Fleet System ID is required');
      return;
    }

    if (!formData.fleetSysVehicleId.trim()) {
      toast.error('Fleet System Vehicle ID is required');
      return;
    }

    if (!formData.regNo.trim()) {
      toast.error('Registration Number is required');
      return;
    }

    if (!formData.make.trim()) {
      toast.error('Make is required');
      return;
    }

    if (!formData.model.trim()) {
      toast.error('Model is required');
      return;
    }

    if (!formData.year.trim()) {
      toast.error('Year is required');
      return;
    }

    if (!formData.color.trim()) {
      toast.error('Color is required');
      return;
    }

    if (!formData.permitNumber.trim()) {
      toast.error('Permit Number is required');
      return;
    }

    if (!formData.vendorId) {
      toast.error('Vendor is required');
      return;
    }

    if (!formData.regionalOffice) {
      toast.error('Regional Office is required');
      return;
    }

    // Convert date strings to timestamps
    const submitData = {
      ...formData,
      insuranceExpiry: new Date(formData.insuranceExpiry).getTime(),
      roadTaxExpiry: new Date(formData.roadTaxExpiry).getTime(),
      fitnessExpiry: new Date(formData.fitnessExpiry).getTime(),
      pucExpiry: new Date(formData.pucExpiry).getTime(),
      permitExpiry: new Date(formData.permitExpiry).getTime()
    };

    createMutation.mutate(submitData);
  };

  const fuelOptions = [
    { value: 'PETROL', label: 'Petrol' },
    { value: 'DIESEL', label: 'Diesel' },
    { value: 'CNG', label: 'CNG' },
    { value: 'ELECTRIC', label: 'Electric' },
    { value: 'HYBRID', label: 'Hybrid' }
  ];

  const typeOptions = [
    { value: 'HATCHBACK', label: 'Hatchback' },
    { value: 'SEDAN', label: 'Sedan' },
    { value: 'SUV', label: 'SUV' },
    { value: 'MUV', label: 'MUV' },
    { value: 'LUXURY', label: 'Luxury' },
    { value: 'BIKE', label: 'Bike' },
    { value: 'AUTO', label: 'Auto' },
    { value: 'ELECTRIC_AUTO', label: 'Electric Auto' }
  ];

  return (
    <div className="page-content">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-header-left">
            <button
              onClick={() => navigate('/vehicles')}
              className="btn btn-outline mr-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </button>
            <div className="page-title-icon">
              <Car className="h-6 w-6" />
            </div>
            <div>
              <h1 className="page-title">Add New Vehicle</h1>
              <p className="page-description">
                Create a new vehicle in the system
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Vehicle Information</h2>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="form-section">
              <h3 className="form-section-title">Basic Information</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Fleet System ID *</label>
                  <input
                    type="text"
                    value={formData.fleetSysId}
                    onChange={(e) => handleInputChange('fleetSysId', e.target.value)}
                    className="form-input"
                    placeholder="Enter fleet system ID"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Fleet System Vehicle ID *</label>
                  <input
                    type="text"
                    value={formData.fleetSysVehicleId}
                    onChange={(e) => handleInputChange('fleetSysVehicleId', e.target.value)}
                    className="form-input"
                    placeholder="Enter fleet system vehicle ID"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Registration Number *</label>
                  <input
                    type="text"
                    value={formData.regNo}
                    onChange={(e) => handleInputChange('regNo', e.target.value)}
                    className="form-input"
                    placeholder="Enter registration number"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Make *</label>
                  <input
                    type="text"
                    value={formData.make}
                    onChange={(e) => handleInputChange('make', e.target.value)}
                    className="form-input"
                    placeholder="Enter vehicle make"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Model *</label>
                  <input
                    type="text"
                    value={formData.model}
                    onChange={(e) => handleInputChange('model', e.target.value)}
                    className="form-input"
                    placeholder="Enter vehicle model"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Year *</label>
                  <input
                    type="text"
                    value={formData.year}
                    onChange={(e) => handleInputChange('year', e.target.value)}
                    className="form-input"
                    placeholder="Enter vehicle year"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Color *</label>
                  <input
                    type="text"
                    value={formData.color}
                    onChange={(e) => handleInputChange('color', e.target.value)}
                    className="form-input"
                    placeholder="Enter vehicle color"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Fuel Type *</label>
                  <select
                    value={formData.fuel}
                    onChange={(e) => handleInputChange('fuel', e.target.value)}
                    className="form-input"
                    required
                  >
                    {fuelOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Vehicle Type *</label>
                  <select
                    value={formData.type}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                    className="form-input"
                    required
                  >
                    {typeOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Permit & Documents */}
            <div className="form-section">
              <h3 className="form-section-title">Permit & Documents</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Permit Number *</label>
                  <input
                    type="text"
                    value={formData.permitNumber}
                    onChange={(e) => handleInputChange('permitNumber', e.target.value)}
                    className="form-input"
                    placeholder="Enter permit number"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Permit Expiry *</label>
                  <input
                    type="date"
                    value={formData.permitExpiry}
                    onChange={(e) => handleInputChange('permitExpiry', e.target.value)}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Insurance Expiry *</label>
                  <input
                    type="date"
                    value={formData.insuranceExpiry}
                    onChange={(e) => handleInputChange('insuranceExpiry', e.target.value)}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Fitness Expiry *</label>
                  <input
                    type="date"
                    value={formData.fitnessExpiry}
                    onChange={(e) => handleInputChange('fitnessExpiry', e.target.value)}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">PUC Expiry *</label>
                  <input
                    type="date"
                    value={formData.pucExpiry}
                    onChange={(e) => handleInputChange('pucExpiry', e.target.value)}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Road Tax Expiry *</label>
                  <input
                    type="date"
                    value={formData.roadTaxExpiry}
                    onChange={(e) => handleInputChange('roadTaxExpiry', e.target.value)}
                    className="form-input"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Assignment */}
            <div className="form-section">
              <h3 className="form-section-title">Assignment</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Vendor *</label>
                  <select
                    value={formData.vendorId}
                    onChange={(e) => handleInputChange('vendorId', e.target.value)}
                    className="form-input"
                    required
                  >
                    <option value="">Select a vendor</option>
                    {vendorsData?.data?.vendors?.map((vendor) => (
                      <option key={vendor._id} value={vendor._id}>
                        {vendor.VendorName} - {vendor.fleetSysId}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Regional Office *</label>
                  <select
                    value={formData.regionalOffice}
                    onChange={(e) => handleInputChange('regionalOffice', e.target.value)}
                    className="form-input"
                    required
                  >
                    <option value="">Select a regional office</option>
                    {regionalOfficesData?.data?.regionalOffices?.map((office) => (
                      <option key={office._id} value={office._id}>
                        {office.name} - {office.city}, {office.state}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Documents */}
            <div className="form-section">
              <h3 className="form-section-title">Documents</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Vehicle Photo</label>
                  <input
                    type="file"
                    onChange={(e) => handleFileChange('vehiclePhoto', e.target.files[0])}
                    className="form-input"
                    accept="image/*"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">RC Book</label>
                  <input
                    type="file"
                    onChange={(e) => handleFileChange('vehicleRcDoc', e.target.files[0])}
                    className="form-input"
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Insurance</label>
                  <input
                    type="file"
                    onChange={(e) => handleFileChange('insurance', e.target.files[0])}
                    className="form-input"
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Fitness Certificate</label>
                  <input
                    type="file"
                    onChange={(e) => handleFileChange('fitnessDoc', e.target.files[0])}
                    className="form-input"
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">PUC Certificate</label>
                  <input
                    type="file"
                    onChange={(e) => handleFileChange('pucDoc', e.target.files[0])}
                    className="form-input"
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Permit Document</label>
                  <input
                    type="file"
                    onChange={(e) => handleFileChange('permitDoc', e.target.files[0])}
                    className="form-input"
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Road Tax Document</label>
                  <input
                    type="file"
                    onChange={(e) => handleFileChange('roadTaxDoc', e.target.files[0])}
                    className="form-input"
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="form-actions">
              <button
                type="button"
                onClick={() => navigate('/vehicles')}
                className="btn btn-outline"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createMutation.isLoading}
                className="btn btn-primary"
              >
                {createMutation.isLoading ? 'Creating...' : 'Create Vehicle'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateVehicle; 